import { useSearch } from "@tanstack/react-router";

import { SectionDivider } from "@/components/cinch/SectionDivider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MarkdownView } from "@/lib/markdown";

import { CroForecastFilterBar } from "../components/FilterBar";
import { CroForecastSubNav } from "../components/SubNav";
import { type CroForecastSearch } from "../search";

const FORECAST_MEETING_FLOW = `
The **Forecast Meeting** tab is the weekly input surface — every other
tab summarises what gets entered here.

### What you (Stan) enter each week

1. **Open the Forecast Meeting tab** for the current ISO week. The header
   shows the week range and a *Lock after Wk ends* button — that button
   only enables once the week's Sunday has passed (the snapshot stays
   editable mid-week so corrections aren't lost).
2. **Click a rep's row** to open their drawer. The drawer shows three
   things side-by-side:
   - **Forecast Submission** — pulled live from HubSpot's \`forecast\`
     CRM object for the current fiscal month (this is the AE's own call,
     not yours). The deals included in their submission are listed under
     "N deals in this forecast" with HubSpot deep-links.
   - **CRO Call** — your monthly + quarterly call per segment (Direct
     NL, Direct Reseller, Upsell Reseller, Upsell & Replacement). Past
     months are read-only and show the closed total instead.
   - **Reasoning** — your free-text note for why the number sits where
     it does. Feeds the Phase-2 LLM corpus, so it pays off later.
3. **Save** — the row's *Total CRO Call* + segment splits land in
   \`weekly_cro_calls\` keyed by \`(iso_year, iso_week, rep_id,
   target_period_type, target_period, target_segment)\`. Re-saving the
   same week is an upsert, not a duplicate.
4. **Items of Business** — anything mid-deal that doesn't fit a single
   AE row goes into the meeting agenda at the bottom (resolved/unresolved
   items, mismatch flags, no-decision watchers).

### How that becomes the Summary tab

The Summary tab composes the latest call per \`(rep_id, target_segment)\`
for the month and quarter you're viewing, and presents:

- **PER-SEGMENT REVENUE — \`MMM\` / Q#** table
  - **Plan** ← \`budget_plan\` (set on the Admin tab; rarely changes
    after year-start).
  - **Forecast** ← sum of every AE's latest \`weekly_cro_calls.call_cents\`
    for that \`(target_year, target_period_type, target_period, segment)\`.
    Legacy team-total calls (no segment specified) fold into Direct NL so
    you never see all zeros.
  - **Upside** ← reserved column. Wired through the schema but currently
    always 0; once we add per-deal "upside" tags this column will light
    up.
  - **Closed** ← see "Where Closed / Renewals / Churn come from" below.

If a row's Forecast looks wrong on Summary, the fix is on Forecast
Meeting — open the AE drawer for the month in question, edit, save.
The Summary tab invalidates and re-renders.
`;

const DATA_SOURCES = `
Each Summary card has a specific source. None of the values are typed
into Summary directly.

### New Revenue (Closed)

| Display segment | Cinch contract category |
|---|---|
| Direct NL | \`New\` |
| Direct Reseller | \`Purchase Order\` (Cinch -> reseller) |
| Upsell Reseller | \`Reseller Agreement\` (reseller -> end-customer) |
| Upsell & Replacement | \`Upsell\` + \`Replacement\` |

**Source:** the Cinch API (\`/billing/contract\`, proxied as the
logged-in user). Snowflake's \`billing.billing_contracts\` mart is the
nightly fallback when the API is unreachable. The \`signed_at\` /
\`closed_at\` timestamps decide which fiscal month/quarter each contract
counts toward.

### Renewals (CS Forecast)

Read straight through from the **Renewals** feature — there is no
parallel \`cs_forecasts\` table inside \`cro_forecast\`. J-P (VP Product)
keeps the per-account % chance up to date inside Renewals; the bridge
service (\`renewals_bridge.compute_forecast_month\`) re-uses that
calculation and lights up the CS-Expansion forecast cell on Summary +
the Churn / Contraction / Cancellation breakdown below it.

If a renewal looks miscategorised on Summary, the edit lives **inside
the Renewals feature**, not here.

### Churn / Contraction

**Source:** Snowflake \`bi.revenue_waterfall\` — the same mart Renewals
uses for ground-truth actuals. Cinch_ops doesn't store its own churn
ledger; if a number looks wrong, the upstream waterfall is the
authoritative answer.

### Contracts → Closed by Segment

Same Cinch API path as New Revenue (above). The segment map lives in
\`backend/app/features/cro_forecast/constants.py\` — adding a new
contract category requires editing that file + a quick deploy.

### Open Pipeline / Top of Funnel / Pipeline Movement

All three come from the **HubSpot mart** in Snowflake (\`cinch_marts.hubspot.*\`):

- *Open Pipeline / Open Deals* — \`hubspot_deals WHERE is_open_count = 1\`,
  counted **SQO on**: on the Sales Pipeline that means Sales Qualified,
  Technical Validation, Contract Negotiation and Verbal Commit, with
  Demo - Scheduled excluded — an unqualified deal isn't pipeline yet. The
  Partner (Reseller Deals) and Customer Success pipelines count every open
  stage, because their ladders have no agreed SQO equivalent. This applies
  to the Summary banner totals and to Total / Quarter Pipeline on the
  Forecast Meeting table alike, so the two always agree.
- *TOF* — \`hs_report_tof_results_sales_only\` per company-week.
- *Pipeline Movement* — \`hubspot_lifecycle_events\` joined to
  \`hubspot_deals\` for the SQO+ stages.
- *Forecast Submission column on Forecast Meeting* — live HubSpot REST
  API call against the \`forecast\` CRM object (not the deal-projected
  sum — that's a different surface and doesn't match the HubSpot UI).

### When things look wrong

| Symptom | Where to look |
|---|---|
| Summary forecast column 0 for a segment you called | Forecast Meeting → AE drawer → did you select the right segment? |
| Past-week page shows blank / "Locked, no snapshot" | The cron snapshot for that week didn't fire. Ping me — it's a backfill, not a bug fix. |
| AE's Forecast Submission doesn't match HubSpot | Force-refresh the page; the column reads live HubSpot. If it still differs, check that the AE submitted in HubSpot's *Forecasting* view, not their pipeline view. |
| Renewals / churn number disagrees with Renewals feature | Renewals is the source of truth. Edit there; Summary will reflect on next load. |
| Contract closed doesn't show on Summary | Verify the contract's \`signed_at\` date in the Cinch API; mis-dated contracts land in the wrong month. |
| A rep's Total Pipeline is lower than their HubSpot deal list | Expected — pipeline is SQO on. Open the AE drawer: pre-Sales-Qualified deals are listed but tagged "not in pipeline total". |
| Total open pipeline shows no week-over-week chip for one week | Expected the week the SQO-on definition shipped — the prior snapshot holds an all-stages total, so the two aren't comparable. It returns the following week. |
`;

const LOCKING_FLOW = `
### Lock semantics

The dashboard has three states for any given week:

- **Live** — the week is current or future. Numbers refresh on every
  page load; you can edit AE rows freely.
- **Frozen (Mon @ 19:00 MT)** — the cron has captured the
  payload and stored it in \`weekly_snapshots\`, but Stan hasn't clicked
  Lock yet. The viewed numbers are the snapshot's, *but* you can still
  edit on Forecast Meeting (Stan might need to correct a number after
  the Monday meeting).
- **Locked** — you clicked the *Lock after Wk ends* button and confirmed
  the modal. The week is immutable; all tabs render the locked
  snapshot. Only \`cro_forecast.admin\` can unlock.

A month-final lock fires automatically on the 1st of each month at
00:05 MT — that captures the closing month's numbers with \`is_locked =
true\` + \`is_month_final = true\` so any retroactive edits to source
data can't bend the historical record.
`;

const DEAL_HEALTH_SCORING = `
The **Deal Health** tab scores every open deal across three pipelines —
**Sales**, **Upsells (the Customer Success pipeline)**, and **Partners** —
on five 0–100 axes and assigns a health band. Nothing is typed in by
hand — every input comes from HubSpot (via the nightly Snowflake mart),
and the scores recompute on every page load.

### Where the data comes from

Emails and calls aren't linked to deals in HubSpot — they're linked to the
deal's **company** (emails directly; calls via the contact who was called).
So all engagement counts are company-level: for the usual case of one open
deal per company they're the same thing. "Last touch" is the most recent
of: last email, last call, or HubSpot's own company activity rollup (which
also covers meetings, notes, and tasks we don't sync as tables).

### The five axes

**Engagement** — *how much two-way activity, relative to everyone else.*

- Activity (90 days) = outbound emails + **2×** inbound emails + **2×** calls.
  A customer writing back or getting on the phone is worth double us
  sending mail.
- That activity number is **percentile-ranked against the open deals in
  the same pipeline** (60% of the score) — so "engaged" always means
  *relative to peers of the same motion*: a Partners conversation competes
  with other Partners conversations, not with the sales team's email
  volume.
- The other 40% is recency: touched today = 100, fading linearly to 0 at
  45 days of silence.

**Deal Quality** — *structural strength of the deal itself.*

| Component | Weight | 0 looks like | 100 looks like |
|---|---|---|---|
| Multi-threading | 35% | 0 contacts on the account | 5+ contacts |
| Meeting completion | 25% | meetings only flaked (no-show/cancel) | every meeting held (no history = neutral 50) |
| Stage depth | 20% | first stage of its pipeline | last open stage of its pipeline |
| Hygiene | 20% | no amount, no fresh next step | amount set **and** next step updated ≤30d |

Stage depth uses each pipeline's own ladder (Demo → Contract Negotiation
for Sales; Exploration → Contract Sent for Upsells; Exploration →
Proposal/Negotiation for Partners). Partners deals never carry an amount
in HubSpot, so their hygiene score is judged on next-step freshness alone
— they aren't docked for a field the pipeline doesn't use.

**Urgency** — *how much it needs attention now.* Driven by the close date:
due today = 100, fading to 0 at 90+ days out; **overdue pegs at 100**; no
close date at all = 30. Closing within 30 days on a stale next step
(untouched >14d, or missing) adds +15.

**Momentum** — *stage progress freshness.* Advanced to the current stage
this week = 100, fading to 0 at 90 days stuck. No stage timestamp = 50.

**Multi-thread** — contact breadth on its own (same 5-contact cap as the
quality component), so a single-threaded deal is visible on the radar even
when quality is otherwise fine.

### The headline score

\`Health = 45% Engagement + 35% Deal Quality + 20% Momentum\`

Urgency deliberately stays **out** of the headline — an urgent deal isn't a
worse deal — it shows on the radar and drives the band rules instead.

### Health bands (rules, not a model)

Checked in order; each deal shows *why* in its detail panel:

1. **Stalled** — no touch in 30+ days (or never), **or** 90+ days stuck in
   the current stage. A stalled deal can have a busy rep — 9 months in
   Sales Qualified still counts as stalled on purpose.
2. **At Risk** — close date already slipped past, engagement below 35, or
   closing within 30 days with engagement below 55.
3. **Healthy** — everything else.

### On the landscape chart

X = Engagement, Y = Deal Quality, bubble area = deal amount, color = band.
Deals before Sales Qualified usually have no amount yet — they render at a
fixed minimum bubble size rather than pretending to have a value.

### Caveats

- Engagement counts are shared across a company's deals (rare, but a
  company with two open deals shows the same activity on both).
- The weights are **hand-tuned v1**. The plan of record is to re-fit them
  against our ~1,900 closed deals (327 won) so the score reflects what
  actually predicted wins at Cinch. The formulas live in
  \`backend/app/features/cro_forecast/deal_health.py\`.
- Mart refreshes nightly; raw rows are cached 4h server-side. The
  "refreshed" date at the bottom of the tab tells you what you're seeing.
`;

export function CroForecastHowToPage() {
  const search = useSearch({ from: "/cro-forecast/how-to" }) as CroForecastSearch;

  return (
    <div className="flex flex-col">
      <CroForecastFilterBar search={search} />
      <CroForecastSubNav search={search} />

      <div className="flex max-w-4xl flex-col gap-6 p-4 md:p-6">
        <header>
          <h2 className="text-lg font-semibold">How to use the CRO Forecast</h2>
          <p className="text-sm text-muted-foreground">
            Editor-only reference. The Forecast Meeting tab is the input
            surface — every other tab is a view onto what gets entered
            there, joined with HubSpot, Cinch contracts, and Renewals.
          </p>
        </header>

        <SectionDivider
          label="1. Forecast Meeting → Summary workflow"
          description="The weekly flow that drives the Summary tab"
        />
        <Card>
          <CardContent className="pt-6">
            <MarkdownView source={FORECAST_MEETING_FLOW} />
          </CardContent>
        </Card>

        <SectionDivider
          label="2. Where the other Summary numbers come from"
          description="Renewals, Churn, Contracts, HubSpot pipeline"
        />
        <Card>
          <CardContent className="pt-6">
            <MarkdownView source={DATA_SOURCES} />
          </CardContent>
        </Card>

        <SectionDivider
          label="3. Snapshot, freeze, and lock"
          description="What the lock states mean"
        />
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Lock states + cron schedule</CardTitle>
          </CardHeader>
          <CardContent>
            <MarkdownView source={LOCKING_FLOW} />
          </CardContent>
        </Card>

        <SectionDivider
          label="4. Deal Health scoring"
          description="How the five axes, headline score, and bands are calculated"
        />
        <Card>
          <CardContent className="pt-6">
            <MarkdownView source={DEAL_HEALTH_SCORING} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
